/**
 * CrmResolve — shared presentation for the Zoho CRM deep-link entry point.
 *
 * Every PazPortal app exposes `/crm/account/:zohoAccountId`. A Zoho CRM Web
 * Tab opens that URL in a new browser tab (see docs/zoho-crm-web-tabs.md);
 * the app resolves the Zoho Account id to a PazPortal customer and redirects.
 *
 * This component renders only the three non-success states, so each app keeps
 * its own data access and its own destination route.
 *
 * Deliberately NOT an iframe embed: Zoho Web Tabs render cross-site, and
 * browsers partition localStorage by top-level site, so the Supabase session
 * (persistSession: true, localStorage) would not be visible inside the frame
 * and the user would appear signed out. Opening in a new tab reuses the real
 * session. See docs/zoho-crm-web-tabs.md for the full rationale.
 */
import type { ReactNode } from 'react';

export type CrmResolveStatus = 'resolving' | 'not-found' | 'error';

export interface CrmResolveProps {
  status: CrmResolveStatus;
  /** The Zoho Account id from the URL, shown so an operator can cross-check. */
  zohoAccountId?: string;
  /** Error detail, rendered only when status is 'error'. */
  message?: string;
  /** Optional escape hatch, e.g. a link back to the app's own list view. */
  action?: ReactNode;
}

export function CrmResolve({ status, zohoAccountId, message, action }: CrmResolveProps) {
  const copy: Record<CrmResolveStatus, { title: string; body: string }> = {
    resolving: {
      title: 'Opening in PazPortal',
      body: 'Matching this Zoho account to a PazPortal customer…',
    },
    'not-found': {
      title: 'No matching customer',
      body:
        'This Zoho account has no linked PazPortal customer, or it is outside ' +
        'your access. It may not have synced yet — Accounts sync every 15 minutes.',
    },
    error: {
      title: 'Could not open this account',
      body: message ?? 'Something went wrong resolving the Zoho account.',
    },
  };

  const { title, body } = copy[status];

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-lg border border-[var(--border)] bg-[var(--surface)] p-6 text-center">
        {status === 'resolving' && (
          <div
            aria-hidden="true"
            className="mx-auto mb-4 h-6 w-6 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--primary)]"
          />
        )}
        <h1 className="text-base font-semibold text-[var(--text)]">{title}</h1>
        <p className="mt-2 text-sm text-[var(--text-muted)]">{body}</p>
        {zohoAccountId && (
          <p className="mt-4 font-mono text-xs text-[var(--text-faint)]">
            Zoho account {zohoAccountId}
          </p>
        )}
        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  );
}
